import { gearFromDto, gearsToInput, sightingFromDto, sightingsToInput, type GearRow, type SightingRow } from './sightings';
import type { ExpenseDto, SeaClass, VoyageDetail, VoyageNewContext } from './api';

export const ZONE_SLOTS = 10;

export type DayRow = { surveyDate: string; surveyHours: number | null; zones: (number | null)[]; surveyClass: '' | SeaClass; impression: string };

export type ExpenseForm = {
  fuelDate: string;
  fuelType: string;
  fuelTaxRounding: string;
  fuelQuantity: number | null;
  fuelUnitPrice: number | null;
  fuelLightOilTax: number | null;
  fuelAmountExclTax: number | null;
  fuelTax: number | null;
  fuelTotal: number | null;
  fuelNote: string;
  lubeDate: string;
  lubeUnitPrice: number | null;
  lubeCoefficient: number | null;
  lubeAmountExclTax: number | null;
  lubeTotal: number | null;
  devDate: string;
  devTaxRounding: string;
  devAmountExclTax: number | null;
  devTax: number | null;
  devTotal: number | null;
};

export type VoyageForm = {
  periodFrom: string;
  periodTo: string;
  captainName: string;
  surveyor2Name: string;
  surveyor3Name: string;
  bearerCount: number;
  ownerName: string;
  isInvoiceTargetSurvey: boolean;
  catchSurveyStatus: string;
  reporterName: string;
  note: string;
  surveyDays: DayRow[];
  sightings: SightingRow[];
  gearFindings: GearRow[];
  expense: ExpenseForm;
};

const s = (v: string | null | undefined) => v ?? '';
const zonesOf = (z: number[]) => Array.from({ length: ZONE_SLOTS }, (_, i) => z[i] ?? null);

export function dayRow(date: string, hours = 8): DayRow {
  return { surveyDate: date, surveyHours: hours, zones: zonesOf([]), surveyClass: '', impression: '' };
}

export function emptyExpense(d?: { fuelType: string | null; fuelTaxRounding: string; devTaxRounding: string }): ExpenseForm {
  return {
    fuelDate: '',
    fuelType: d?.fuelType ?? '',
    fuelTaxRounding: d?.fuelTaxRounding ?? '四捨五入',
    fuelQuantity: null,
    fuelUnitPrice: null,
    fuelLightOilTax: null,
    fuelAmountExclTax: null,
    fuelTax: null,
    fuelTotal: null,
    fuelNote: '',
    lubeDate: '',
    lubeUnitPrice: null,
    lubeCoefficient: null,
    lubeAmountExclTax: null,
    lubeTotal: null,
    devDate: '',
    devTaxRounding: d?.devTaxRounding ?? '四捨五入',
    devAmountExclTax: null,
    devTax: null,
    devTotal: null,
  };
}

function expenseFromDto(e: ExpenseDto): ExpenseForm {
  return {
    fuelDate: s(e.fuelDate),
    fuelType: s(e.fuelType),
    fuelTaxRounding: e.fuelTaxRounding,
    fuelQuantity: e.fuelQuantity,
    fuelUnitPrice: e.fuelUnitPrice,
    fuelLightOilTax: e.fuelLightOilTax || null,
    fuelAmountExclTax: e.fuelAmountExclTax || null,
    fuelTax: e.fuelTax || null,
    fuelTotal: e.fuelTotal || null,
    fuelNote: s(e.fuelNote),
    lubeDate: s(e.lubeDate),
    lubeUnitPrice: e.lubeUnitPrice,
    lubeCoefficient: e.lubeCoefficient,
    lubeAmountExclTax: e.lubeAmountExclTax || null,
    lubeTotal: e.lubeTotal || null,
    devDate: s(e.devDate),
    devTaxRounding: e.devTaxRounding,
    devAmountExclTax: e.devAmountExclTax || null,
    devTax: e.devTax || null,
    devTotal: e.devTotal || null,
  };
}

export function formFromContext(c: VoyageNewContext): VoyageForm {
  return {
    periodFrom: c.prefill.periodFrom,
    periodTo: c.prefill.periodTo,
    captainName: c.prefill.captainName,
    surveyor2Name: '',
    surveyor3Name: '',
    bearerCount: 0,
    ownerName: c.prefill.ownerName,
    isInvoiceTargetSurvey: false,
    catchSurveyStatus: '',
    // kintone 検証指摘：報告者氏名の初期値は船主
    reporterName: c.prefill.ownerName ?? '',
    note: '',
    surveyDays: c.surveyDays.map((d) => dayRow(d.surveyDate, d.surveyHours)),
    sightings: c.sightings.map(sightingFromDto),
    gearFindings: c.gearFindings.map(gearFromDto),
    expense: emptyExpense(c.expenseDefaults),
  };
}

export function formFromDetail(d: VoyageDetail): VoyageForm {
  return {
    periodFrom: d.periodFrom,
    periodTo: d.periodTo,
    captainName: d.captainName,
    surveyor2Name: s(d.surveyor2Name),
    surveyor3Name: s(d.surveyor3Name),
    bearerCount: d.bearerCount,
    ownerName: s(d.ownerName),
    isInvoiceTargetSurvey: d.isInvoiceTargetSurvey,
    catchSurveyStatus: s(d.catchSurveyStatus),
    reporterName: s(d.reporterName),
    note: s(d.note),
    surveyDays: d.surveyDays.map((x) => ({ surveyDate: x.surveyDate, surveyHours: x.surveyHours, zones: zonesOf(x.zoneNumbers), surveyClass: x.surveyClass ?? '', impression: s(x.impression) })),
    sightings: d.sightings.map(sightingFromDto),
    gearFindings: d.gearFindings.map(gearFromDto),
    expense: d.expense ? expenseFromDto(d.expense) : emptyExpense(),
  };
}

const nz = (v: string) => (v.trim() === '' ? null : v);
const n0 = (v: number | null) => v ?? 0;

/** API 送信用の body */
export function toVoyageInput(f: VoyageForm, noticeVesselId: number, confirmations: Record<string, boolean>, id?: number) {
  const e = f.expense;
  return {
    id,
    noticeVesselId,
    periodFrom: f.periodFrom,
    periodTo: f.periodTo,
    captainName: nz(f.captainName),
    surveyor2Name: nz(f.surveyor2Name),
    surveyor3Name: nz(f.surveyor3Name),
    bearerCount: Number(f.bearerCount) || 0,
    ownerName: nz(f.ownerName),
    isInvoiceTargetSurvey: f.isInvoiceTargetSurvey,
    catchSurveyStatus: f.catchSurveyStatus || null,
    reporterName: nz(f.reporterName),
    note: nz(f.note),
    surveyDays: f.surveyDays.map((d) => ({
      surveyDate: d.surveyDate || null,
      surveyHours: d.surveyHours,
      zoneNumbers: d.zones.filter((z): z is number => z != null),
      surveyClass: d.surveyClass || null,
      impression: nz(d.impression),
    })),
    sightings: sightingsToInput(f.sightings),
    gearFindings: gearsToInput(f.gearFindings),
    expense: {
      fuelDate: e.fuelDate || null,
      fuelType: e.fuelType || null,
      fuelTaxRounding: e.fuelTaxRounding,
      fuelQuantity: e.fuelQuantity,
      fuelUnitPrice: e.fuelUnitPrice,
      fuelLightOilTax: n0(e.fuelLightOilTax),
      fuelAmountExclTax: n0(e.fuelAmountExclTax),
      fuelTax: n0(e.fuelTax),
      fuelTotal: n0(e.fuelTotal),
      fuelNote: nz(e.fuelNote),
      lubeDate: e.lubeDate || null,
      lubeUnitPrice: e.lubeUnitPrice,
      lubeAmountExclTax: n0(e.lubeAmountExclTax),
      lubeTotal: n0(e.lubeTotal),
      devDate: e.devDate || null,
      devTaxRounding: e.devTaxRounding,
      devAmountExclTax: n0(e.devAmountExclTax),
      devTax: n0(e.devTax),
      devTotal: n0(e.devTotal),
    },
    confirmations,
  };
}

/** 期間内の日付一覧 */
export function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  if (!from || !to || from > to) return out;
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end && out.length < 120) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/**
 * 調査水域を変更した行の調査判定を、計算結果の行区分（行内の S＞A＞B）に合わせる（kintone 検証指摘：
 * 調査水域を変更しても自動の調査水域（調査判定）が変わらない）。変更していない行は利用者の修正値を保持する。
 */
export function applyRowClasses(days: DayRow[], changedRows: ReadonlySet<number>, calcDays: { surveyDate: string; rowClass: SeaClass | null }[]): DayRow[] {
  const byDate = new Map(calcDays.map((d) => [d.surveyDate, d.rowClass]));
  return days.map((d, i) => (changedRows.has(i) && d.surveyDate && byDate.has(d.surveyDate) ? { ...d, surveyClass: byDate.get(d.surveyDate) ?? '' } : d));
}
